<template>
  <div class="flow-page">
    <div class="flow-head">
      <img
        class="flow-back"
        src="@/assets/icons/i-return.png"
        alt="返回"
        title="返回"
        @click="$emit('back')"
      />
      <div class="flow-head-title">{{ headTitle }}</div>
      <div class="flow-head-sub">{{ recordId ? "聊天记录" : "AI 要素确认" }}</div>
    </div>

    <!-- 对话体 + 底部 dock：白色整体，撑满剩余高度 -->
    <div class="flow-main">
      <div ref="flowBodyRef" class="flow-body">
        <div class="chat-column">
          <!-- 用户原始输入（流程模式；记录模式历史里已含原始输入） -->
          <div v-if="!recordId" class="chat-user">
            <div class="chat-user-bubble">{{ input.text }}</div>
          </div>

          <!-- 接口返回的动态对话 -->
          <template v-for="(m, i) in chatLog" :key="i">
            <div v-if="m.role === 'user'" class="chat-user">
              <div class="chat-user-bubble small">{{ m.text }}</div>
            </div>
            <div v-else class="ai-row">
              <div class="ai-avatar">AI</div>
              <div class="ai-content">
                <div
                  class="ai-text tight markdown-body"
                  v-html="renderMd(m.text)"
                ></div>
              </div>
            </div>
          </template>

          <!-- AI 正在输出 -->
          <div v-if="aiTyping" class="ai-row">
            <div class="ai-avatar">AI</div>
            <div class="ai-content">
              <div class="ai-text tight"><TypingDots /></div>
            </div>
          </div>
        </div>
      </div>

      <div class="flow-bottom">
        <div class="flow-bottom-inner">
          <!-- 生成的文书卡片（记录模式，Detail 查到文书数据时展示在聊天框上方） -->
          <div v-if="docReady" class="element-complete-card">
            <div class="element-complete-icon">✓</div>
            <div class="element-complete-copy">
              <div class="element-complete-title">
                要素式起诉状已生成<span
                  v-if="docUpdated"
                  class="element-complete-updated"
                  >文件已更新</span
                >
              </div>
              <div class="element-complete-sub">
                已根据当前案件信息完成文书生成，可下载查看完整内容。
              </div>
            </div>
            <div class="element-complete-actions">
              <button
                type="button"
                class="element-complete-view"
                @click="$emit('view-doc', docRecordId)"
              >
                <svg class="icon sm"><use href="#eb-file" /></svg>查看文书
              </button>
              <button
                type="button"
                class="element-complete-download"
                :disabled="downloading"
                @click="downloadDoc"
              >
                <svg class="icon sm"><use href="#eb-download" /></svg>下载文书
              </button>
            </div>
          </div>

          <!-- 对话输入：补充信息 / 回答 AI 的问题 -->
          <div class="flow-compose">
            <textarea
              v-model="extraText"
              placeholder="输入补充信息或回答 AI 的问题……"
              @keydown.enter.exact.prevent="sendMessage"
            ></textarea>
            <div class="flow-compose-foot">
              <button type="button" class="flow-send" @click="sendMessage">
                <svg class="icon sm"><use href="#eb-send" /></svg>
              </button>
            </div>
          </div>

          <div class="flow-disclaimer">
            内容由 AI 生成，仅供参考，请注意甄别。
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: "GenerateFlow" });
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { message } from "ant-design-vue";
import { marked } from "marked";
import TypingDots from "./TypingDots.vue";
import {
  resetElementChat,
  initElementChat,
  sendElementMessageStream,
  elementChatId,
} from "../store";
import {
  getIndictmentConversationDetail,
  getElementChatHistoryPage,
  saveIndictmentDoc,
} from "@/api/elementBased";

// input: { text, files: [{ name, label, size }], sourceType: text|material|complaint }
// 记录查看模式（列表「查看」进入）：recordId 为记录 id（Detail / 下载用），
// chatId 为 aiChatsId（查历史 / 续聊用），title 为记录名
const props = defineProps({
  input: { type: Object, required: true },
  recordId: { type: String, default: "" },
  chatId: { type: String, default: "" },
  title: { type: String, default: "" },
});
defineEmits(["back", "view-doc"]);

// 顶部标题：记录模式用记录名；
// 有上传文件时展示文件名（材料全文在 text 里，不宜直接展示）；
// 纯文字流程用原始描述压缩成单行（62 字截断）
const headTitle = computed(() => {
  const t = props.recordId
    ? props.title || "聊天记录"
    : (props.input.files || []).map((f) => f.name).join("、") ||
      (props.input.text || "").replace(/\n+/g, " ").trim();
  return t.length > 62 ? t.slice(0, 62) + "…" : t;
});

// ---------- 动态对话（接口数据驱动，无本地文案） ----------
const chatLog = ref([]); // { role: 'user' | 'ai', text }
const extraText = ref("");
const flowBodyRef = ref(null);
// AI 回复等待中的打字指示
const aiTyping = ref(false);

// ---------- 生成的文书卡片（聊天框上方，Detail 查询成功即展示） ----------
const docReady = ref(false);
const docRecordId = ref(""); // 下载 / 查看用：记录 id 或聊天中生成返回的 id
const downloading = ref(false);
const docFileUrl = ref(""); // 上一次 Detail 的文件地址：重查时对比判断文件是否更新
const docUpdated = ref(false); // 「文件已更新」标注（展示 10 秒）
let docUpdatedTimer = null;

// 查文书详情：查到数据即展示卡片（不阻塞聊天，失败只记日志）。
// refresh=true 时强制重查：聊天补充信息后文书重新生成，文件地址已更新
const fetchDocDetail = async (id, { refresh = false } = {}) => {
  if (!id) return;
  if (docReady.value && !refresh) return;
  try {
    const detail = await getIndictmentConversationDetail(id);
    const d = detail?.data ?? {};
    if (d && Object.keys(d).length > 0) {
      const newUrl = d.fileDownloadUrl ?? d.FileDownloadUrl ?? "";
      // 重新生成重查：对比前后文件地址，不同才标「文件已更新」（展示 10 秒）
      if (refresh && docFileUrl.value && newUrl && newUrl !== docFileUrl.value) {
        docUpdated.value = true;
        clearTimeout(docUpdatedTimer);
        docUpdatedTimer = setTimeout(() => (docUpdated.value = false), 10000);
      }
      docFileUrl.value = newUrl;
      docRecordId.value = String(id);
      docReady.value = true;
    }
  } catch (err) {
    console.error("[GenerateFlow] 查询文书详情失败:", err);
  }
};

// 下载文书：直接用文书记录 id 走真实下载
const downloadDoc = async () => {
  if (downloading.value || !docRecordId.value) return;
  downloading.value = true;
  try {
    await saveIndictmentDoc({
      id: docRecordId.value,
      name: props.title || "要素式起诉状",
    });
    message.success("文件下载成功");
  } catch (err) {
    console.error("[GenerateFlow] 下载文书失败:", err);
    message.error("文书下载失败，请稍后重试");
  } finally {
    downloading.value = false;
  }
};

// 记录模式：加载该记录的历史消息（whereId 为 aiChatsId，
// 倒序分页取完 reverse 转正序；无 chatId 时回落记录 id）
const loadHistory = async () => {
  const rows = [];
  const pageSize = 50;
  for (let page = 1; page <= 20; page++) {
    const res = await getElementChatHistoryPage({
      whereId: props.chatId || props.recordId,
      pageNum: page,
      pageSize,
    });
    const part = res?.data?.data ?? [];
    rows.push(...part);
    if (part.length < pageSize) break;
  }
  rows
    .filter((x) => x.content)
    .reverse()
    .forEach((x) =>
      chatLog.value.push({
        role: x.isSend === true ? "user" : "ai",
        text: x.content,
      }),
    );
  scrollFlowBottom();
};

const scrollFlowBottom = () => {
  setTimeout(() => {
    const el = flowBodyRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  }, 40);
};

const pushLog = (role, text) => {
  chatLog.value.push({ role, text });
  scrollFlowBottom();
};

// ---------- AI 回复 Markdown 渲染（marked + 流式表格修复，同 casesearch/WorkbenchRight） ----------
marked.setOptions({ gfm: true, breaks: true });

// 检测行是否包含表格单元格（排除代码块）
const isTableLine = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("```") || trimmed.startsWith("~~~")) return false;
  return trimmed.includes("|");
};

// 检测行是否是表格分隔线
const isSeparatorLine = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return false;
  let content = trimmed;
  if (content.startsWith("|")) content = content.slice(1);
  if (content.endsWith("|")) content = content.slice(0, -1);
  const matches = content.match(/:?-{3,}:?/g);
  return matches !== null && matches.length >= 1;
};

// 解析表格行
const parseTableRow = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return { cells: [], count: 0 };
  let content = trimmed;
  const startsWithPipe = content.startsWith("|");
  const endsWithPipe = content.endsWith("|");
  if (startsWithPipe) content = content.slice(1);
  if (endsWithPipe) content = content.slice(0, -1);
  const cells = content.split("|").map((c) => c.trim());
  return { cells, count: cells.length, startsWithPipe, endsWithPipe };
};

// 修复表格行格式
const fixTableRowLine = (cells, colCount) => {
  while (cells.length < colCount) cells.push("");
  if (cells.length > colCount) cells = cells.slice(0, colCount);
  return "| " + cells.join(" | ") + " |";
};

// 流式 Markdown 表格修复：处理分隔线缺 |、列数不齐、缺分隔线、未闭合等问题
// （打字机逐字释放期间表格长期处于半成品状态，不修会整段掉回纯文本）
const fixStreamMarkdown = (text) => {
  if (!text) return "";
  const lines = text.split("\n");
  const result = [];
  let i = 0;
  let inCodeBlock = false;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed.startsWith("```") || trimmed.startsWith("~~~")) {
      inCodeBlock = !inCodeBlock;
      result.push(line);
      i++;
      continue;
    }

    if (inCodeBlock) {
      result.push(line);
      i++;
      continue;
    }

    if (isTableLine(line)) {
      if (result.length > 0 && result[result.length - 1].trim() !== "") {
        result.push("");
      }

      const tableLines = [];
      let j = i;
      while (j < lines.length) {
        const tl = lines[j];
        const tTrimmed = tl.trim();
        if (tTrimmed.startsWith("```") || tTrimmed.startsWith("~~~")) break;
        if (isTableLine(tl)) {
          tableLines.push(tl);
          j++;
        } else if (tTrimmed === "") {
          let k = j + 1;
          while (k < lines.length && lines[k].trim() === "") k++;
          if (k < lines.length && isTableLine(lines[k])) {
            tableLines.push(tl);
            j++;
          } else break;
        } else break;
      }

      const cleanLines = tableLines.filter((l) => l.trim() !== "");
      if (cleanLines.length > 0) {
        let colCount = 0;
        cleanLines.forEach((cl) => {
          const parsed = parseTableRow(cl);
          colCount = Math.max(colCount, parsed.count);
          if (isSeparatorLine(cl)) {
            const matches = cl.match(/:?-{3,}:?/g);
            if (matches) colCount = Math.max(colCount, matches.length);
          }
        });
        colCount = Math.max(colCount, 1);

        const hasSeparator = cleanLines.some((cl) => isSeparatorLine(cl));

        const headerParsed = parseTableRow(cleanLines[0]);
        result.push(fixTableRowLine(headerParsed.cells, colCount));
        result.push("|" + " :--- |".repeat(colCount));

        const dataStart = hasSeparator ? 2 : 1;
        for (let d = dataStart; d < cleanLines.length; d++) {
          const dp = parseTableRow(cleanLines[d]);
          result.push(fixTableRowLine(dp.cells, colCount));
        }

        if (j < lines.length && lines[j].trim() !== "") result.push("");
        i = j;
      } else {
        result.push(line);
        i++;
      }
    } else {
      result.push(line);
      i++;
    }
  }

  // 末行表格未闭合（流式截断）：补齐缺少的 |
  if (result.length > 0 && !inCodeBlock) {
    const last = result[result.length - 1];
    const lastTrim = last.trim();
    if (lastTrim.startsWith("|") && !lastTrim.endsWith("|")) {
      for (let k = result.length - 2; k >= 0; k--) {
        const prev = result[k];
        if (isTableLine(prev) && prev.trim().endsWith("|")) {
          const prevCols = (prev.match(/\|/g) || []).length - 1;
          const currCols = (last.match(/\|/g) || []).length;
          if (prevCols >= currCols) {
            result[result.length - 1] = last + " |".repeat(prevCols - currCols + 1);
          }
          break;
        }
      }
    }
  }

  return result.join("\n");
};

const renderMd = (s) => (s ? marked.parse(fixStreamMarkdown(s)) : "");

// ---------- 打字机平滑释放 ----------
// 后端 aimsg 事件粒度大（一整段一推，一段一跳），把到达的文本先入队，
// 再按固定节奏逐字释放到气泡，还原流式观感；
// 速率自适应积压量：落后的字越多每次释放越多，长回复不会拖太久
let twTimer = null; // setInterval 句柄
let twReply = null; // 正在释放的气泡对象（chatLog 内，深响应可变）
let twTarget = ""; // 已到达的全量文本
let twShown = 0; // 已释放显示的字符数

const twStart = () => {
  if (twTimer) return;
  twTimer = setInterval(() => {
    if (!twReply) return;
    if (twShown < twTarget.length) {
      const step = Math.max(2, Math.ceil((twTarget.length - twShown) / 60));
      twShown = Math.min(twTarget.length, twShown + step);
      twReply.text = twTarget.slice(0, twShown);
      scrollFlowBottom();
    } else {
      clearInterval(twTimer);
      twTimer = null;
    }
  }, 16);
};

// 立即释放剩余全部并停表（发送结束/出错/组件卸载时兜底，不丢字）
const twFlush = () => {
  if (twTimer) {
    clearInterval(twTimer);
    twTimer = null;
  }
  if (twReply) {
    twReply.text = twTarget;
    twReply = null;
  }
};

// 等队列释放完毕（Add 返回后打字机可能还没打完）
const twWaitDrain = async () => {
  while (twTimer) await new Promise((r) => setTimeout(r, 40));
};

onBeforeUnmount(() => {
  twFlush();
  clearTimeout(docUpdatedTimer);
});

// 发送补充信息 / 回答 AI 问题：先上屏再发送
const sendMessage = () => {
  const t = extraText.value.trim();
  if (!t || aiTyping.value) return;
  pushLog("user", t);
  extraText.value = "";
  sendAndStream(t);
};

// ---------- 与 AI 的持续交互（建对话 + AIChatHistorys/Add 流式，见 store.js） ----------

// 进页初始化：
// 记录查看模式 → 续用该记录的对话（elementChatId = aiChatsId），
//   Detail（记录 id）查文书状态，GetPageData（aiChatsId）加载历史消息；
//   后续在输入框发消息即续聊
// 流程模式 → 建新会话，把案件描述发给 AI，流式展示 AI 的分析回复
onMounted(async () => {
  if (props.recordId) {
    elementChatId.value = props.chatId || props.recordId;
    await fetchDocDetail(props.recordId);
    try {
      await loadHistory();
    } catch (err) {
      console.error("[GenerateFlow] 加载聊天记录失败:", err);
      message.error("聊天记录获取失败，请稍后重试");
    }
    return;
  }
  // 新流程新对话
  resetElementChat();
  try {
    await initElementChat();
  } catch (err) {
    console.error("[GenerateFlow] 创建会话失败:", err);
    return;
  }
  await sendAndStream(
    props.input.text,
    (props.input.files || []).map((f) => f.name).join(","),
  );
});

// 发送一条消息并流式展示 AI 回复（初始描述 / 补充 / 作答共用）：
// 等待首段增量时显示打字指示；增量先入打字机队列逐字释放；
// hub 没连上时 onChunk 不触发，用 Add 返回的完整记录走同样的打字机兜底
const sendAndStream = async (content, fileNames = "") => {
  aiTyping.value = true;
  scrollFlowBottom();
  twFlush(); // 上一条若有残留立即放完，避免两条同时打字
  twTarget = "";
  twShown = 0;

  let reply = null; // 本次流式接收的 AI 回复条目
  try {
    const record = await sendElementMessageStream(content, fileNames, {
      onChunk: (msg) => {
        twTarget += msg;
        if (!reply) {
          aiTyping.value = false;
          chatLog.value.push({ role: "ai", text: "" });
          // 必须取数组里的响应式代理再改：直接改 push 进去的原始对象
          // 不触发渲染（页面不更新的根因）
          reply = chatLog.value[chatLog.value.length - 1];
          twReply = reply;
        }
        twStart();
      },
    });
    if (!reply) {
      // 兜底：hub 未连上，Add 返回的最终回复记录也走打字机
      const text = record?.content;
      if (text) {
        aiTyping.value = false;
        chatLog.value.push({ role: "ai", text: "" });
        reply = chatLog.value[chatLog.value.length - 1];
        twReply = reply;
        twTarget = text;
        twStart();
      }
    }
    // Add 返回 indictmentGenerated=true：文书已生成（含补充信息后重新生成），
    // 以 indictmentGenerated 为准重新查详情拿最新文件地址，
    // 文书卡片展示在聊天框上方（不阻塞打字机）。
    // id 优先取返回的 indictmentRecordId；未带回时沿用已知文书 id
    // （上一次生成的 docRecordId / 记录模式进入的 recordId），同一记录重查即最新文件
    if (record?.indictmentGenerated) {
      const regenId =
        record.indictmentRecordId ??
        record.IndictmentRecordId ??
        docRecordId.value ??
        props.recordId;
      // TODO 调试用：确认重查触发后移除
      console.log(
        "[GenerateFlow] indictmentGenerated=true, 重查文书详情, id =",
        regenId,
      );
      if (regenId) fetchDocDetail(String(regenId), { refresh: true });
    }
    await twWaitDrain(); // 打完再放开输入，避免回复交叉
  } catch (err) {
    console.error("[GenerateFlow] 发送失败:", err);
    message.error("发送失败，请检查网络后重试");
  } finally {
    twFlush();
    aiTyping.value = false;
    scrollFlowBottom();
  }
};
</script>

<style scoped>
.flow-page {
  height: calc(100vh - 112px);
  min-height: 640px;
  background: #f5f7fb;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- 顶部信息条 ---------- */
.flow-head {
  height: 54px;
  min-height: 54px;
  border: 1px solid #e5ebf3;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  flex: none;
  background: #fff;
  box-shadow: 0 2px 8px rgba(31, 53, 88, 0.025);
  z-index: 5;
}
.flow-back {
  width: 36px;
  height: 36px;
  flex: none;
  cursor: pointer;
  display: block;
}
.flow-head-title {
  font-size: 14px;
  font-weight: 700;
  color: #25364f;
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.flow-head-sub {
  font-size: 12px;
  color: #8794a7;
  white-space: nowrap;
}

/* ---------- 对话主内容 + 底部 dock：白色整体撑满剩余高度 ---------- */
.flow-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 16px;
  box-shadow: 0 2px 9px rgba(31, 53, 88, 0.025);
  overflow: hidden;
}
.flow-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 28px 0;
}
.chat-column {
  width: min(860px, 92%);
  margin: 0 auto;
}
.chat-user {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 26px;
}
.chat-user-bubble {
  max-width: 760px;
  background: #f4f5f7;
  border-radius: 16px 16px 6px 16px;
  padding: 18px 20px;
  color: #26374e;
  font-size: 14px;
  line-height: 1.75;
  white-space: pre-line;
}
.chat-user-bubble.small {
  padding: 12px 16px;
  max-width: 520px;
}
.ai-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 20px 0;
}
.ai-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1d6eff, #4c8cff);
  color: #fff;
  font-size: 11px;
  font-weight: 850;
  display: grid;
  place-items: center;
  flex: none;
}
.ai-content {
  flex: 1;
  min-width: 0;
  padding-top: 2px;
}
.ai-text {
  margin-top: 9px;
  color: #53647c;
  font-size: 14px;
  line-height: 1.75;
  white-space: pre-line;
}
.ai-text.tight {
  margin-top: 4px;
}

/* AI 回复 markdown 样式（v-html 内容不带 scoped 属性，子选择器需 :deep） */
.ai-text.markdown-body {
  white-space: normal;
}
:deep(.markdown-body p) {
  margin: 8px 0;
}
:deep(.markdown-body h1),
:deep(.markdown-body h2),
:deep(.markdown-body h3),
:deep(.markdown-body h4) {
  margin: 14px 0 8px;
  color: #203452;
  line-height: 1.5;
}
:deep(.markdown-body h1) {
  font-size: 16px;
}
:deep(.markdown-body h2) {
  font-size: 15px;
}
:deep(.markdown-body h3) {
  font-size: 14px;
}
:deep(.markdown-body h4) {
  font-size: 13px;
}
:deep(.markdown-body ul),
:deep(.markdown-body ol) {
  padding-left: 20px;
  margin: 8px 0;
}
:deep(.markdown-body li) {
  margin: 4px 0;
}
:deep(.markdown-body strong) {
  color: #25364f;
}
:deep(.markdown-body blockquote) {
  margin: 8px 0;
  padding: 6px 12px;
  border-left: 3px solid #c9d9f2;
  background: #f6f9ff;
  color: #53647c;
  border-radius: 0 8px 8px 0;
}
:deep(.markdown-body :not(pre) > code) {
  background: #f0f4fa;
  color: #1d5ccc;
  padding: 1px 6px;
  border-radius: 5px;
  font-size: 12.5px;
}
:deep(.markdown-body pre) {
  background: #f6f8fc;
  border: 1px solid #e5ebf3;
  border-radius: 10px;
  padding: 12px;
  overflow: auto;
  margin: 10px 0;
}
:deep(.markdown-body pre code) {
  font-size: 12.5px;
}
:deep(.markdown-body table) {
  border-collapse: collapse;
  margin: 10px 0;
  width: 100%;
  font-size: 13px;
}
:deep(.markdown-body th),
:deep(.markdown-body td) {
  border: 1px solid #e1e8f2;
  padding: 7px 10px;
  text-align: left;
}
:deep(.markdown-body th) {
  background: #f4f7fc;
  color: #25364f;
}
:deep(.markdown-body tr:hover td) {
  background: #f8fbff;
}
:deep(.markdown-body hr) {
  border: 0;
  border-top: 1px solid #e5ebf3;
  margin: 14px 0;
}
:deep(.markdown-body a) {
  color: #1768ff;
}

/* ---------- 底部 dock ---------- */
/* 生成的文书卡片（聊天框上方） */
.element-complete-card {
  margin-bottom: 10px;
  padding: 13px 16px;
  border: 1px solid #d5e5ff;
  border-radius: 12px;
  background: #f0f6ff;
  display: flex;
  align-items: center;
  gap: 12px;
}
.element-complete-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1d6eff, #4c8cff);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  display: grid;
  place-items: center;
  flex: none;
}
.element-complete-copy {
  flex: 1;
  min-width: 0;
}
.element-complete-title {
  font-size: 13.5px;
  font-weight: 750;
  color: #1f3d7a;
}
/* 「文件已更新」标注：标题右侧小标签，展示 10 秒 */
.element-complete-updated {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #eaf8f2;
  color: #16825e;
  font-size: 10px;
  font-weight: 700;
  vertical-align: 1px;
}
.element-complete-sub {
  font-size: 12px;
  color: #5a77a8;
  margin-top: 3px;
}
.element-complete-actions {
  display: flex;
  gap: 8px;
  flex: none;
}
.element-complete-view,
.element-complete-download {
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.16s;
  white-space: nowrap;
}
.element-complete-view {
  border: 1px solid #bcd4ff;
  background: #fff;
  color: #2f6bff;
}
.element-complete-view:hover {
  background: #eaf2ff;
}
.element-complete-download {
  border: 1px solid #1768ff;
  background: #1768ff;
  color: #fff;
}
.element-complete-download:hover {
  background: #0e59db;
}
.element-complete-download:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.flow-bottom {
  flex: none;
  padding-bottom: 6px;
}
.flow-bottom-inner {
  width: min(860px, 92%);
  margin: 0 auto;
}
.flow-compose {
  border: 1px solid #e1e7ef;
  border-radius: 16px;
  background: #fff;
  padding: 11px 12px 9px;
}
.flow-compose textarea {
  width: 100%;
  height: 54px;
  resize: none;
  border: 0;
  outline: 0;
  padding: 2px 2px 6px;
  color: #263b58;
  font-size: 13px;
  line-height: 1.65;
  font-family: inherit;
}
.flow-compose textarea::placeholder {
  color: #9ca7b6;
}
.flow-compose-foot {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}
.flow-send {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: #1768ff;
  color: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.flow-send:hover {
  background: #0e59db;
}
.flow-disclaimer {
  font-size: 11px;
  color: #a0aabb;
  text-align: center;
  margin-top: 9px;
}

/* ---------- 通用 ---------- */
.icon {
  width: 20px;
  height: 20px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.icon.sm {
  width: 16px;
  height: 16px;
}
</style>
